import { useEffect, useMemo, useState } from 'react';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { usePermission } from '@backstage/plugin-permission-react';
import Alert from '@material-ui/lab/Alert';
import { UserManagementClient } from './api';
import { EditGithubUsernameDialog, UserRowData } from './popup';
import { catalogUserUpdatePermission } from './permissions';

export function ProfileGithubCard() {
  const identity = useApi(identityApiRef);
  const catalog = useApi(catalogApiRef);
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const client = useMemo(
    () => new UserManagementClient(discovery, fetchApi),
    [discovery, fetchApi],
  );
  const [user, setUser] = useState<UserRowData>();
  const [error, setError] = useState<Error>();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profiles, setProfiles] = useState<Entity[]>([]);
  const [selectedRef, setSelectedRef] = useState('');
  const [missingProfile, setMissingProfile] = useState(false);
  const { allowed, loading: loadingPermission } = usePermission({
    permission: catalogUserUpdatePermission,
  });

  useEffect(() => {
    let active = true;
    async function load() {
      setUser(undefined);
      setError(undefined);
      setSaved(false);
      const { userEntityRef } = await identity.getBackstageIdentity();
      const targetRef = selectedRef || userEntityRef;
      const ref = parseEntityRef(targetRef);
      const entity = await catalog.getEntityByRef(targetRef);
      if (!active) return;
      if (!entity) {
        setMissingProfile(true);
        if (allowed) {
          const result = await catalog.getEntities({
            filter: { kind: 'User' },
          });
          if (active) setProfiles(result.items);
        }
        return;
      }
      const mapping = await client.getGithubUsername(ref.name, ref.namespace);
      const profile = entity.spec?.profile as
        | { displayName?: string; email?: string }
        | undefined;
      if (active)
        setUser({
          name: ref.name,
          namespace: ref.namespace,
          entityRef: targetRef,
          displayName:
            profile?.displayName || entity.metadata.title || ref.name,
          email: profile?.email,
          memberOf:
            entity.relations
              ?.filter(r => r.type === 'memberOf')
              .map(r => r.targetRef) || [],
          profileUrl: `/catalog/${ref.namespace}/user/${ref.name}`,
          githubUsername: mapping.githubUsername || undefined,
        });
    }
    load().catch(err => {
      if (active) setError(err instanceof Error ? err : new Error(String(err)));
    });
    return () => {
      active = false;
    };
  }, [identity, catalog, client, selectedRef, allowed]);

  return (
    <InfoCard title="GitHub username">
      {error && <ResponseErrorPanel error={error} />}
      {missingProfile && (
        <Box mb={2}>
          <Alert severity="info">
            Your signed-in account has no user profile in the Catalog. Sign in
            with your corporate account to update your GitHub username.
            {allowed &&
              ' You can also select a Catalog user to manage their mapping.'}
          </Alert>
          {allowed && (
            <Box mt={2}>
              <TextField
                id="github-mapping-catalog-user"
                select
                fullWidth
                variant="outlined"
                label="Catalog user"
                value={selectedRef}
                onChange={event => {
                  setEditing(false);
                  setSelectedRef(event.target.value);
                }}
                helperText={
                  profiles.length
                    ? 'Select the profile whose GitHub username you want to update.'
                    : 'No Catalog users available. Check the HR Catalog synchronization.'
                }
              >
                {profiles.map(profile => (
                  <MenuItem
                    key={stringifyEntityRef(profile)}
                    value={stringifyEntityRef(profile)}
                  >
                    {profile.metadata.title || profile.metadata.name} (
                    {stringifyEntityRef(profile)})
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Box>
      )}
      {!user &&
        !error &&
        (!missingProfile || loadingPermission || Boolean(selectedRef)) && (
          <Progress />
        )}
      {user && (
        <>
          {saved && (
            <Box mb={2}>
              <Alert severity="success">
                GitHub username saved. Catalog updates may take a moment.
              </Alert>
            </Box>
          )}
          <Typography>
            {user.githubUsername || 'No GitHub username configured'}
          </Typography>
          <Box mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setEditing(true)}
            >
              Edit GitHub username
            </Button>
          </Box>
          <EditGithubUsernameDialog
            open={editing}
            user={user}
            client={client}
            onClose={() => setEditing(false)}
            onSuccess={updated => {
              setUser({
                ...user,
                githubUsername: updated.githubUsername || undefined,
              });
              setSaved(true);
            }}
          />
        </>
      )}
    </InfoCard>
  );
}
